import { useEffect, useRef, useState } from 'react'
import { STRIPE_PUBLISHABLE_KEY } from '../../lib/stripeKey'

/**
 * Structured address entry for the signup wizard.
 *
 * The address is load-bearing: the server resolves the buyer's country
 * (ISO-2 → Heera CountryId) from it, which drives the pricing-markup and
 * office-routing machinery. So no freeform textarea — a manual STRUCTURED
 * form (street / suite / city / state / postal / country) whose fields are
 * all visible at once.
 *
 * ⚠️ The Stripe Address Element path is retained but GATED OFF
 * (`USE_STRIPE_ADDRESS`, Tej 2026-09-12): its autocomplete collapses to just
 * a country + a single "address" line and only reveals city/state/postal after
 * you pick an autocomplete suggestion — real users read that as "it only asks
 * for line 1" and can't enter a full address. The manual form asks for the
 * actual address plainly. Flip the flag back on only once the element is
 * configured to render all fields up front. Both paths produce the same
 * WizardAddress shape.
 */

/** Stripe Address Element OFF for signup — see the note above. */
const USE_STRIPE_ADDRESS = false

export interface WizardAddress {
  line1: string
  line2?: string
  city: string
  state?: string
  postalCode?: string
  /** ISO-3166 alpha-2, e.g. "US" — the systematic anchor for CountryId. */
  country: string
  countryName?: string
}

export function regionName(iso2: string): string | undefined {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(iso2.toUpperCase()) ?? undefined
  } catch {
    return undefined
  }
}

// Trade centers first (matches the phone step's dial codes), then A–Z.
const COUNTRIES: Array<[string, string]> = [
  ['US', 'United States'], ['IN', 'India'], ['AE', 'United Arab Emirates'],
  ['BE', 'Belgium'], ['IL', 'Israel'], ['HK', 'Hong Kong'], ['GB', 'United Kingdom'],
  ['SG', 'Singapore'], ['TH', 'Thailand'], ['JP', 'Japan'], ['AU', 'Australia'], ['ZA', 'South Africa'],
  ['AR', 'Argentina'], ['AT', 'Austria'], ['BD', 'Bangladesh'], ['BR', 'Brazil'], ['BW', 'Botswana'],
  ['CA', 'Canada'], ['CH', 'Switzerland'], ['CL', 'Chile'], ['CN', 'China'], ['CO', 'Colombia'],
  ['CZ', 'Czechia'], ['DE', 'Germany'], ['DK', 'Denmark'], ['EG', 'Egypt'], ['ES', 'Spain'],
  ['FI', 'Finland'], ['FR', 'France'], ['GR', 'Greece'], ['HU', 'Hungary'], ['ID', 'Indonesia'],
  ['IE', 'Ireland'], ['IT', 'Italy'], ['JO', 'Jordan'], ['KE', 'Kenya'], ['KR', 'South Korea'],
  ['KW', 'Kuwait'], ['LB', 'Lebanon'], ['LK', 'Sri Lanka'], ['LU', 'Luxembourg'], ['MX', 'Mexico'],
  ['MY', 'Malaysia'], ['NA', 'Namibia'], ['NL', 'Netherlands'], ['NO', 'Norway'], ['NZ', 'New Zealand'],
  ['OM', 'Oman'], ['PA', 'Panama'], ['PE', 'Peru'], ['PH', 'Philippines'], ['PK', 'Pakistan'],
  ['PL', 'Poland'], ['PT', 'Portugal'], ['QA', 'Qatar'], ['RO', 'Romania'], ['SA', 'Saudi Arabia'],
  ['SE', 'Sweden'], ['TR', 'Türkiye'], ['TW', 'Taiwan'], ['UA', 'Ukraine'], ['UY', 'Uruguay'], ['VN', 'Vietnam'],
]

export function AddressStep({ value, onChange, companyName }: {
  value: WizardAddress | null
  onChange: (a: WizardAddress | null) => void
  companyName?: string
}) {
  const mountRef = useRef<HTMLDivElement>(null)
  // 'loading' → trying Stripe; 'stripe' → element mounted; 'manual' → fallback.
  // Signup uses the manual structured form (USE_STRIPE_ADDRESS off, see above).
  const [mode, setMode] = useState<'loading' | 'stripe' | 'manual'>(
    USE_STRIPE_ADDRESS && STRIPE_PUBLISHABLE_KEY ? 'loading' : 'manual',
  )
  const [manual, setManual] = useState<WizardAddress>(
    value ?? { line1: '', line2: '', city: '', state: '', postalCode: '', country: '' },
  )

  // Mount the Stripe Address Element when a key is available. Any failure
  // (script blocked, bad key, no network) degrades to the manual form —
  // address entry can never dead-end the wizard.
  useEffect(() => {
    if (!USE_STRIPE_ADDRESS || !STRIPE_PUBLISHABLE_KEY) return
    let disposed = false
    let elements: { getElement?: unknown; destroy?: () => void } | null = null
    void (async () => {
      try {
        const { loadStripe } = await import('@stripe/stripe-js')
        const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY!)
        if (!stripe || disposed || !mountRef.current) { setMode('manual'); return }
        const els = stripe.elements({
          appearance: {
            variables: {
              colorPrimary: '#c9a94a',
              colorText: '#0a1f3c',
              colorTextPlaceholder: '#6b83a3',
              colorBackground: '#ffffff',
              borderRadius: '4px',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSizeBase: '16px',
            },
          },
        })
        const addressEl = els.create('address', {
          mode: 'billing',
          defaultValues: value
            ? { address: {
                line1: value.line1, line2: value.line2 ?? '', city: value.city,
                state: value.state ?? '', postal_code: value.postalCode ?? '',
                country: value.country || 'US',
              } }
            : undefined,
        })
        addressEl.on('change', (ev) => {
          if (ev.complete) {
            const a = ev.value.address
            onChange({
              line1: a.line1,
              line2: a.line2 ?? undefined,
              city: a.city,
              state: a.state ?? undefined,
              postalCode: a.postal_code ?? undefined,
              country: a.country,
              countryName: regionName(a.country),
            })
          } else {
            onChange(null)
          }
        })
        addressEl.on('loaderror', () => setMode('manual'))
        addressEl.mount(mountRef.current)
        elements = els as unknown as { destroy?: () => void }
        setMode('stripe')
      } catch {
        if (!disposed) setMode('manual')
      }
    })()
    return () => {
      disposed = true
      try { (elements as { destroy?: () => void } | null)?.destroy?.() } catch { /* unmounted */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function patchManual(p: Partial<WizardAddress>) {
    const nextVal = { ...manual, ...p }
    setManual(nextVal)
    const complete = nextVal.line1.trim() && nextVal.city.trim() && nextVal.country
    onChange(complete
      ? {
          line1: nextVal.line1.trim(),
          line2: nextVal.line2?.trim() || undefined,
          city: nextVal.city.trim(),
          state: nextVal.state?.trim() || undefined,
          postalCode: nextVal.postalCode?.trim() || undefined,
          country: nextVal.country,
          countryName: COUNTRIES.find(([c]) => c === nextVal.country)?.[1] ?? regionName(nextVal.country),
        }
      : null)
  }

  return (
    <div>
      <h1 className="font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
        {companyName ? `Where is ${companyName} located?` : 'Where are you located?'}
      </h1>
      <p className="mt-2 text-sm text-navy-500">
        Your country sets duties and shipping — so this one we need exactly right.
      </p>

      {/* Stripe element mounts here; hidden when the fallback took over. */}
      <div ref={mountRef} className={mode === 'stripe' ? 'mt-8' : 'hidden'} data-testid="signup-address-stripe" />
      {mode === 'loading' && (
        <div className="mt-8 space-y-3" aria-hidden>
          <div className="skeleton h-12 rounded-sm" />
          <div className="skeleton h-12 rounded-sm" />
        </div>
      )}

      {mode === 'manual' && (
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="signup-address-manual">
          <input type="text" placeholder="Street address" autoComplete="address-line1" value={manual.line1}
            onChange={(e) => patchManual({ line1: e.target.value })}
            className="input h-[52px] text-base sm:col-span-2" data-testid="signup-address-line1" />
          <input type="text" placeholder="Suite, floor, building (optional)" autoComplete="address-line2" value={manual.line2 ?? ''}
            onChange={(e) => patchManual({ line2: e.target.value })}
            className="input h-[52px] text-base sm:col-span-2" />
          <input type="text" placeholder="City" autoComplete="address-level2" value={manual.city}
            onChange={(e) => patchManual({ city: e.target.value })}
            className="input h-[52px] text-base" data-testid="signup-address-city" />
          <input type="text" placeholder="State / province" autoComplete="address-level1" value={manual.state ?? ''}
            onChange={(e) => patchManual({ state: e.target.value })}
            className="input h-[52px] text-base" />
          <input type="text" placeholder="Postal code" autoComplete="postal-code" value={manual.postalCode ?? ''}
            onChange={(e) => patchManual({ postalCode: e.target.value })}
            className="input h-[52px] text-base" />
          <select
            value={manual.country}
            onChange={(e) => patchManual({ country: e.target.value })}
            className="input h-[52px] text-base"
            aria-label="Country"
            data-testid="signup-address-country"
          >
            <option value="" disabled>Country</option>
            {COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>
      )}
    </div>
  )
}
